import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Box, Container, Typography, Button, Stack, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import CircuitAnimation from './CircuitAnimation';
import { Link as RouterLink } from 'react-router-dom';
import { getFeatureFlags } from '@/config/featureFlags';

const StyledButton = styled(Button)(({ }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  fontSize: '1rem',
  padding: '10px 24px',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
  },
}));

const HeroSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  const featureFlags = getFeatureFlags();

  const opacity = useTransform(
    scrollYProgress,
    [0, isMobile ? 0.5 : 0.8],
    [1, 0],
    {
      clamp: true
    }
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        height: { 
          xs: 'calc(100vh - 56px)', // Adjusted for mobile header
          sm: '100vh' 
        },
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(to bottom, #000000, #1a1133)',
        display: 'flex',
        alignItems: 'flex-end',
        minHeight: {
          xs: '500px', // Minimum height on mobile
          sm: '600px'  // Minimum height on tablet/desktop
        },
      }}
    >
      <CircuitAnimation />
      {/* <ParticleBackground /> */}
      {/* <NetworkCluster /> */}
      
      <Container
        maxWidth={false}
        sx={{
          position: 'relative',
          zIndex: 2,
          pb: { xs: 6, sm: 10, md: 12 },
          pl: { xs: 2, sm: 6, md: 12 },
          pr: { xs: 2, sm: 6, md: 12 }
        }}
      >
        <motion.div
          style={{ opacity }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.6,
            ease: "easeOut"
          }}
        >
          <Box
            sx={{
              maxWidth: '800px',
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'white',
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                lineHeight: 1.2,
                fontWeight: 800,
                mb: 3,
                fontFamily: 'Helvetica, sans-serif',
              }}
            >
              Connecting Students to Their Dreams
            </Typography>
            
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 400,
                lineHeight: 1.5,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                mb: 4,
                maxWidth: '600px',
              }}
            >
              Discover meaningful volunteer opportunities that align with your career aspirations. Turn your community service into stepping stones toward your dream future.
            </Typography>

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
            >
              {featureFlags.enableRegistration && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    component={RouterLink}
                    to="/register"
                    sx={{ textDecoration: 'none' }}
                  >
                    <StyledButton
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Start Your Journey
                    </StyledButton>
                  </Link>
                </motion.div>
              )}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  component={RouterLink}
                  to="/how-it-works/"
                  sx={{ textDecoration: 'none' }}
                >
                  <StyledButton
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                    }}
                  >
                    Learn More
                  </StyledButton>
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  component={RouterLink}
                  to="/contact?reason=requesting_demo"
                  sx={{ textDecoration: 'none' }}
                >
                  <StyledButton
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                    }}
                  >
                    Request a Demo
                  </StyledButton>
                </Link>
              </motion.div>
            </Stack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroSection;